import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./AbandonedCart.css";

const API_URL = process.env.REACT_APP_API_URL || 'https://api.vastrudayam.com/api';

const AbandonedCart = () => {
    const [cartData, setCartData] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchAbandonedCarts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/admin/abandoned-cart`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setCartData(res.data.abandonedCart);
            }
        } catch (error) {
            console.error("Fetch abandoned cart error:", error);
            toast.error("Failed to load abandoned cart data");
        } finally {
            setLoading(false);
        }
    };

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_URL}/admin/coupons`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setCoupons(res.data.coupons.filter(c => c.is_active));
            }
        } catch (error) {
            console.error("Fetch coupons error:", error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to remove this entry?")) return;
        try {
            const token = localStorage.getItem("token");
            const res = await axios.delete(`${API_URL}/admin/abandoned-cart/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                toast.success("Entry removed");
                fetchAbandonedCarts();
            }
        } catch (error) {
            console.error("Delete abandoned cart error:", error);
            toast.error("Failed to delete entry");
        }
    };

    useEffect(() => {
        fetchAbandonedCarts();
        fetchCoupons();
    }, []);

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        let baseUrl = API_URL.replace(/\/api\/?$/, "");
        if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
        const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
        return `${baseUrl}${cleanPath}`;
    };

    const filteredCart = cartData.filter(item =>
        (item.user_name && item.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.user_phone && item.user_phone.includes(searchTerm)) ||
        (item.user_email && item.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.product_name && item.product_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.product_code && item.product_code.toString().includes(searchTerm))
    );

    if (loading) return (
        <div className="admin-abond-loader-overlay">
            <div className="admin-abond-loader-container">
                <div className="admin-abond-spinner"></div>
            </div>
        </div>
    );

    return (
        <div className="admin-abond">
            <div className="admin-abond-header">
                <div>
                    <h3><i className="bi bi-cart-x-fill"></i> Abandoned Carts</h3>
                    <p>View products that customers added to cart but didn't buy (Inactive for &gt; 1hr)</p>
                </div>
                <div className="admin-abond-header-actions">
                    <button className="admin-abond-refresh-btn" onClick={fetchAbandonedCarts} title="Refresh Data">
                        <i className="bi bi-arrow-clockwise"></i>
                    </button>
                    <div className="admin-abond-search-box">
                        <i className="bi bi-search"></i>
                        <input
                            type="text"
                            placeholder="Search by user, phone or product..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Promotional Offers Banner */}
            {coupons.length > 0 && (
                <div className="admin-abond-offers-banner">
                    <div className="admin-abond-banner-content">
                        <div className="admin-abond-banner-text">
                            <h4>🚀 Boost Conversions!</h4>
                            <p>Share these active offers with abandoned cart customers to bring them back.</p>
                        </div>
                        <div className="admin-abond-coupons-scroll">
                            {coupons.map(coupon => (
                                <div key={coupon.id} className="admin-abond-coupon-card">
                                    <span className="admin-abond-coupon-code">{coupon.code}</span>
                                    <span className="admin-abond-coupon-desc">
                                        {coupon.discount_value}{coupon.discount_type === 'percentage' ? '%' : '₹'} OFF
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="admin-abond-table-container">
                <table className="admin-abond-table">
                    <thead>
                        <tr>
                            <th>Product Code</th>
                            <th>Image</th>
                            <th>Product Name</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Customer</th>
                            <th>Contact</th>
                            <th>Last Activity</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCart.length > 0 ? (
                            filteredCart.map((item) => (
                                <tr key={item.cart_entry_id}>
                                    <td><span className="admin-abond-id-badge">{item.product_code}</span></td>
                                    <td>
                                        <div className="admin-abond-table-img-container">
                                            <img src={getImageUrl(item.product_image)} alt={item.product_name} className="admin-abond-table-thumb" />
                                        </div>
                                    </td>
                                    <td>
                                        <span className="admin-abond-prod-name">{item.product_name}</span>
                                    </td>
                                    <td>
                                        <span className="admin-abond-qty-badge">x{item.quantity}</span>
                                    </td>
                                    <td>
                                        <span className="admin-abond-price-text">₹{item.price}</span>
                                    </td>
                                    <td>
                                        <span className="admin-abond-user-name-text">{item.user_name}</span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span className="admin-abond-phone-text">{item.user_phone || "N/A"}</span>
                                            <span style={{ fontSize: '0.7rem', color: '#636e72' }}>{item.user_email}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="admin-abond-date-text">{new Date(item.updated_at).toLocaleString()}</span>
                                    </td>
                                    <td>
                                        <button className="admin-abond-delete-btn" onClick={() => handleDelete(item.cart_entry_id)} title="Remove Entry">
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="admin-abond-empty-table-msg">
                                    {searchTerm ? "No matching abandoned carts found" : "No abandoned carts found"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Stats Cards */}
            <div className="admin-abond-cart-stats">
                <div className="admin-abond-stat-card">
                    <i className="bi bi-people"></i>
                    <div className="admin-abond-stat-info">
                        <h4>{new Set(cartData.map(c => c.user_id)).size}</h4>
                        <span>Unique Users</span>
                    </div>
                </div>
                <div className="admin-abond-stat-card">
                    <i className="bi bi-cart-x"></i>
                    <div className="admin-abond-stat-info">
                        <h4>{cartData.length}</h4>
                        <span>Total Abandoned Items</span>
                    </div>
                </div>
                <div className="admin-abond-stat-card">
                    <i className="bi bi-currency-rupee"></i>
                    <div className="admin-abond-stat-info">
                        <h4>₹{cartData.reduce((acc, item) => acc + (parseFloat(item.price) * item.quantity), 0).toLocaleString()}</h4>
                        <span>Potential Revenue</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AbandonedCart;